import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, NEVER, Observable } from 'rxjs';
import { catchError, distinctUntilChanged, map, tap } from 'rxjs/operators';
import { ElementHistoryLog } from 'src/app/models/domain';
import { SharePointService } from 'src/app/services';
import { handleError } from './akita-error-helper';
import { CollectionState } from './collection-state';

interface ISheet {
  webUrl?: URL;
  worksheetName?: string;
}

@Injectable({ providedIn: 'root' })
export class ElementHistoryLogService {
  private readonly state = new CollectionState<ElementHistoryLog>();
  private readonly pageIndexSubject = new BehaviorSubject(1);
  private readonly pageSizeSubject = new BehaviorSubject(2);
  private readonly sheetSubject = new BehaviorSubject<ISheet>({});

  public readonly loading$ = this.state.loading$;
  public readonly error$ = this.state.error$;
  public readonly log$ = this.state.items$;
  public readonly logSize$ = this.log$.pipe(map(({ length }) => length));
  public readonly pageIndex$ = this.pageIndexSubject.asObservable();
  public readonly pageSize$ = this.pageSizeSubject.asObservable();

  public readonly webUrl$ = this.sheetSubject.pipe(
    map(({ webUrl }) => webUrl),
    distinctUntilChanged()
  );

  public readonly worksheetName$ = this.sheetSubject.pipe(
    map(({ worksheetName }) => worksheetName),
    distinctUntilChanged()
  );

  public readonly paginatedLog$: Observable<ElementHistoryLog[]> = combineLatest([
    this.log$,
    this.pageIndex$,
    this.pageSize$,
  ]).pipe(
    map(([log, pageIndex, pageSize]) => log.slice((pageIndex - 1) * pageSize, pageIndex * pageSize))
  );

  public constructor(private readonly sharePointService: SharePointService) {}

  public loadById(designId: string): Observable<void> {
    this.state.setLoading(true);

    return this.sharePointService.getElementHistoryLogForDesignId(designId).pipe(
      tap((elementLog) => {
        this.sheetSubject.next({
          webUrl: elementLog.webUrl,
          worksheetName: elementLog.worksheetName,
        });

        this.state.set(elementLog.log);
      }),
      catchError((error) => {
        this.sheetSubject.next({});

        handleError(this.state, error, {
          404: () => this.state.setError(String(error)),
        });

        return NEVER;
      }),
      map(() => undefined)
    );
  }

  public updatePageIndex(pageIndex: number) {
    this.pageIndexSubject.next(pageIndex);
  }

  public updatePageSize(pageSize: number) {
    this.pageSizeSubject.next(pageSize);
  }
}
